import React from 'react'
import { toast } from 'react-hot-toast'
import { ActionButton } from '~/components/ActionButton'
import { addContactForSeries } from '~/components/shared/contact'
import { isValidEmailSyntax } from '~/components/shared/email'
import { TextInput } from '~/components/TextInput'
import { Series } from '~/domain/Series'
import styles from './index.module.scss'

interface SeriesCollectionsEmptyStateProps {
  series: Series
}

export const SeriesCollectionsEmptyState: React.FC<SeriesCollectionsEmptyStateProps> = ({ series }) => {
  const [email, setEmail] = React.useState<string | undefined>(undefined)
  const [isEmailSaved, setIsEmailSaved] = React.useState<boolean>(false)
  const [isEmailSaving, setIsEmailSaving] = React.useState<boolean>(false)
  const isButtonDisabled = !email || !isValidEmailSyntax(email) || isEmailSaving || isEmailSaved
  const isInputDisabled = isEmailSaved

  const onEmailChanged = (value: string) => setEmail(value)

  const saveEmail = async () => {
    if (isButtonDisabled) return

    toast.loading('Saving email...')

    try {
      setIsEmailSaving(true)
      await addContactForSeries(email as string, series.id)
    } catch (exception: unknown) {
      toast.dismiss()
      toast.error('Something went wrong! Please try again or let us know if the issue persists')

      return
    } finally {
      setIsEmailSaving(false)
    }

    setIsEmailSaved(true)
    toast.dismiss()
    toast.success(`Great! We'll notify ${email} when ${series.name} is live`)
  }

  return (
    <div
      data-testid="series-collections-empty"
      className={ styles['series-collections-empty-state__container'] }
    >
      <div className={ styles['series-collections-empty-state__card'] }>
        <strong className={ styles['series-collections-empty-state__title'] }>
          This series isn&apos;t live yet, but it will very soon. Don&apos;t miss it!
        </strong>

        <span className={ styles['series-collections-empty-state__description'] }>
          Leave us your email and we&apos;ll let you know as soon as these NFTs are available.
        </span>

        <TextInput
          className={ styles['series-collections-empty-state__email-input'] }
          type="email"
          disabled= { isInputDisabled }
          onChange={ onEmailChanged }
          onEnterPressed={ saveEmail }
        />

        <ActionButton
          size="large"
          hierarchy="primary"
          className={ styles['series-collections-empty-state__send-button'] }
          isDisabled={ isButtonDisabled }
          isLoading={ isEmailSaving }
          onClick={ saveEmail }
        >
          Send
        </ActionButton>
      </div>
    </div>
  )
}
